import { Injectable } from '@angular/core';
import { IUser } from '@core/models/octa.model';
import { BehaviorSubject } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public loggedUser: BehaviorSubject<IUser> = new BehaviorSubject<IUser>({
    name: '',
    email: '',
    sub: '',
    given_name: '',
    family_name: '',
    preferred_username: ''
  });

  /* Expose auth bootstrap state. `null` means auth has not finished initializing yet. */
  private readonly authReadySubject = new BehaviorSubject<boolean | null>(null);
  public authReady$ = this.authReadySubject.asObservable();

  constructor(
    private readonly oauthService: OAuthService
  ) {}

  /* Initialize authentication by loading discovery document and trying login. */
  async initAuth(): Promise<void> {
    try {
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();

      if (!this.oauthService.hasValidAccessToken()) {
        this.oauthService.initCodeFlow();
        return;
      }

      const profile: any = await this.oauthService.loadUserProfile();
      const claims = profile?.info ?? profile;

      this.onLoginSuccess(claims);

      this.authReadySubject.next(true);
    } catch (err) {
      this.authReadySubject.next(false);
    }
  }

  /* check if there is a valid access token to determine if the user is authenticated. */
  hasValidToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  /* get claims from the token to extract user information and roles. */
  getClaims(): any {
    return this.oauthService.getIdentityClaims();
  }

  /* Handle successful login by updating the logged user state. */
  onLoginSuccess(user: IUser): void {
    this.loggedUser.next({
      name: user.name,
      email: user.email,
      sub: user.sub,
      given_name: user.given_name,
      family_name: user.family_name,
      preferred_username: user.preferred_username
    });
  }

  /* Clear user information on logout. */
  clearUser(): void {
    this.loggedUser.next({
      name: '',
      email: '',
      sub: '',
      given_name: '',
      family_name: '',
      preferred_username: ''
    });
  }

  setAuthReady(isReady: boolean): void {
    this.authReadySubject.next(isReady);
  }
}
